import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import papi, { formatErr } from "@/lib/partnerApi";
import PartnerNav from "@/pages/partner/PartnerNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2, Save, Plus, Trash2 } from "lucide-react";

export default function AdminEstimate() {
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (localStorage.getItem("partner_role") !== "admin") { navigate("/login"); return; }
    papi.get("/estimate/config").then((r) => setC(r.data)).catch(() => navigate("/login")).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    try { await papi.put("/estimate/config", c); toast.success("Estimate config saved"); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  const setRate = (i, v) => { const b = [...c.base_rates]; b[i] = { ...b[i], rate: Number(v) }; setC({ ...c, base_rates: b }); };
  const setPkg = (i, key, v) => { const p = [...c.packages]; p[i] = { ...p[i], [key]: key === "pct" ? Number(v) : v }; setC({ ...c, packages: p }); };
  const setUp = (pi, ui, v) => { const p = [...c.packages]; const u = [...p[pi].upgrades]; u[ui] = { ...u[ui], multiplier: Number(v) }; p[pi] = { ...p[pi], upgrades: u }; setC({ ...c, packages: p }); };
  const delPkg = (i) => setC({ ...c, packages: c.packages.filter((_, x) => x !== i) });
  const addPkg = () => setC({ ...c, packages: [...c.packages, { id: crypto.randomUUID(), name: "New Package", pct: 0, upgrades: [{ label: "Standard", multiplier: 1 }, { label: "Premium", multiplier: 1.3 }, { label: "Luxury", multiplier: 1.6 }] }] });
  const setRule = (grp, key, v) => setC({ ...c, [grp]: { ...c[grp], [key]: Number(v) } });

  if (loading || !c) return <div className="min-h-screen bg-[#FBFAF9] flex items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-[#0E9AC8]" /></div>;
  const pctSum = c.packages.reduce((a, p) => a + Number(p.pct || 0), 0);

  return (
    <div className="min-h-screen bg-[#FBFAF9]">
      <PartnerNav actions={
        <Button onClick={save} data-testid="es-save-config" className="bg-[#0E9AC8] hover:bg-[#0B7EA4] text-white rounded-xl"><Save className="h-4 w-4 mr-1.5" /> Save</Button>
      } />
      <main className="max-w-6xl mx-auto px-6 py-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card-premium rounded-2xl p-6">
          <h2 className="font-display text-xl font-bold text-slate-900 mb-4">Base Construction Rates (₹/sqft)</h2>
          <div className="space-y-2">
            {c.base_rates.map((r, i) => (
              <div key={r.id} className="flex items-center gap-3"><span className="text-sm text-slate-700 w-32">{r.name}</span>
                <Input type="number" data-testid={`es-cfg-rate-${r.id}`} className="rounded-xl" value={r.rate} onChange={(e) => setRate(i, e.target.value)} /></div>
            ))}
          </div>
          <h2 className="font-display text-xl font-bold text-slate-900 mt-6 mb-3">Foundation / Plinth / Basement Rules</h2>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <Lbl t="Foundation default depth (ft)"><Input type="number" className="rounded-xl" value={c.foundation.default_depth_ft} onChange={(e) => setRule("foundation", "default_depth_ft", e.target.value)} /></Lbl>
            <Lbl t="Foundation ₹/ft/sqft"><Input type="number" className="rounded-xl" value={c.foundation.extra_rate_per_ft_per_sqft} onChange={(e) => setRule("foundation", "extra_rate_per_ft_per_sqft", e.target.value)} /></Lbl>
            <Lbl t="Plinth default (ft)"><Input type="number" className="rounded-xl" value={c.plinth.default_ft} onChange={(e) => setRule("plinth", "default_ft", e.target.value)} /></Lbl>
            <Lbl t="Plinth ₹/ft/sqft"><Input type="number" className="rounded-xl" value={c.plinth.extra_rate_per_ft_per_sqft} onChange={(e) => setRule("plinth", "extra_rate_per_ft_per_sqft", e.target.value)} /></Lbl>
            <Lbl t="Basement ₹/sqft"><Input type="number" data-testid="es-cfg-basement" className="rounded-xl" value={c.basement.rate_per_sqft} onChange={(e) => setRule("basement", "rate_per_sqft", e.target.value)} /></Lbl>
          </div>
        </div>

        <div className="card-premium rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-xl font-bold text-slate-900">Work Packages <span className={`text-sm font-normal ${pctSum === 100 ? "text-emerald-600" : "text-amber-600"}`}>(Σ {pctSum}%)</span></h2>
            <Button size="sm" onClick={addPkg} variant="outline" className="rounded-lg"><Plus className="h-4 w-4 mr-1" /> Add</Button>
          </div>
          <div className="space-y-3 max-h-[560px] overflow-y-auto">
            {c.packages.map((p, i) => (
              <div key={p.id} className="rounded-xl border border-slate-200 p-3">
                <div className="flex items-center gap-2">
                  <Input className="rounded-lg h-9" value={p.name} onChange={(e) => setPkg(i, "name", e.target.value)} />
                  <Input type="number" className="rounded-lg h-9 w-20" value={p.pct} onChange={(e) => setPkg(i, "pct", e.target.value)} /><span className="text-xs text-slate-400">%</span>
                  <button onClick={() => delPkg(i)} className="text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                </div>
                <div className="flex gap-2 mt-2">
                  {p.upgrades.map((u, ui) => (
                    <div key={ui} className="flex items-center gap-1"><span className="text-xs text-slate-500">{u.label}</span>
                      <Input type="number" step="0.1" className="rounded-lg h-8 w-16 text-xs" value={u.multiplier} onChange={(e) => setUp(i, ui, e.target.value)} /></div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}
const Lbl = ({ t, children }) => <div><label className="text-xs text-slate-500">{t}</label><div className="mt-1">{children}</div></div>;
